import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiPlus, FiPieChart } from 'react-icons/fi';

import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import ErrorBanner from '../../components/ui/ErrorBanner';
import EmptyState from '../../components/ui/EmptyState';
import FormField from '../../components/ui/FormField';
import { Select } from '../../components/ui/Input';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';

import { listBudgets, listBudgetsByDept } from '../../api/budgets';
import { DEPARTMENTS } from '../../auth/permissions';
import { formatPercent, formatCurrency } from '../../utils/currency';

import BudgetForm from './BudgetForm';
import BudgetDetailModal from './BudgetDetailModal';

const DEPARTMENT_OPTIONS = Object.values(DEPARTMENTS);
const ALL_DEPTS = 'ALL';

function buildYearOptions(current) {
  return [current - 2, current - 1, current, current + 1, current + 2];
}

function severityFor(pct) {
  const n = Number(pct) || 0;
  if (n >= 90) return 'bg-red-500';
  if (n >= 70) return 'bg-amber-500';
  return 'bg-green-500';
}

/**
 * Inline progress bar used inside table rows. Width clamped to [0, 100] so
 * over-utilisation still fills the track but never overflows.
 */
function UtilizationBar({ value }) {
  const pct = Number(value) || 0;
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 w-32 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full ${severityFor(pct)} transition-all`}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="w-14 text-right text-xs font-medium tabular-nums text-ink">
        {formatPercent(pct, 1)}
      </span>
    </div>
  );
}

export default function BudgetsListPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [dept, setDept] = useState(ALL_DEPTS);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState(null); // BudgetResponse row

  const yearOptions = useMemo(() => buildYearOptions(currentYear), [currentYear]);

  const allQuery = useQuery({
    queryKey: ['budgets', { year }],
    queryFn: () => listBudgets({ year }),
    enabled: dept === ALL_DEPTS,
  });

  const deptQuery = useQuery({
    queryKey: ['budgets', 'dept', dept, year],
    queryFn: () => listBudgetsByDept(dept, year),
    enabled: dept !== ALL_DEPTS,
  });

  const activeQuery = dept === ALL_DEPTS ? allQuery : deptQuery;
  const rows = activeQuery.data || [];

  const summary = useMemo(() => {
    let allocated = 0;
    let used = 0;
    let remaining = 0;
    for (const b of rows) {
      allocated += Number(b.allocatedAmount || 0);
      used += Number(b.usedAmount || 0);
      remaining += Number(b.remainingAmount || 0);
    }
    const utilization = allocated > 0 ? (used / allocated) * 100 : 0;
    return { allocated, used, remaining, utilization, count: rows.length };
  }, [rows]);

  const columns = [
    {
      key: 'department',
      header: 'Department',
      render: (r) => <span className="font-medium text-ink">{r.department}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      render: (r) => (
        <span className="text-ink">{r.category}</span>
      ),
    },
    {
      key: 'allocatedAmount',
      header: 'Allocated',
      align: 'right',
      render: (r) => <CurrencyDisplay value={r.allocatedAmount} />,
    },
    {
      key: 'usedAmount',
      header: 'Used',
      align: 'right',
      render: (r) => <CurrencyDisplay value={r.usedAmount} />,
    },
    {
      key: 'remainingAmount',
      header: 'Remaining',
      align: 'right',
      render: (r) => {
        const v = Number(r.remainingAmount || 0);
        return (
          <span className={v <= 0 ? 'font-medium text-danger' : 'text-ink'}>
            <CurrencyDisplay value={r.remainingAmount} />
          </span>
        );
      },
    },
    {
      key: 'utilizationPercent',
      header: 'Utilization',
      render: (r) => <UtilizationBar value={r.utilizationPercent} />,
    },
    {
      key: 'year',
      header: 'Year',
      align: 'center',
      render: (r) => <span className="tabular-nums">{r.year}</span>,
    },
    {
      key: 'quarter',
      header: 'Quarter',
      align: 'center',
      render: (r) => (
        <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink">
          Q{r.quarter}
        </span>
      ),
    },
  ];

  return (
    <AppLayout title="Budgets" breadcrumb="Finance / Budgets">
      <PageHeader
        title="Budgets"
        description="Allocate spend by department, category, and quarter — track utilisation in real time."
        actions={
          <Button icon={FiPlus} onClick={() => setCreateOpen(true)}>
            Create Budget
          </Button>
        }
      />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-card">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            Allocated
          </div>
          <div className="mt-1 text-lg font-semibold text-ink">
            {formatCurrency(summary.allocated)}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-card">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            Used
          </div>
          <div className="mt-1 text-lg font-semibold text-ink">
            {formatCurrency(summary.used)}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-card">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            Remaining
          </div>
          <div className="mt-1 text-lg font-semibold text-ink">
            {formatCurrency(summary.remaining)}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-card">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            Avg utilisation
          </div>
          <div className="mt-2">
            <UtilizationBar value={summary.utilization} />
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-card">
        <FormField label="Year" className="w-32">
          <Select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Department" className="w-48">
          <Select value={dept} onChange={(e) => setDept(e.target.value)}>
            <option value={ALL_DEPTS}>All departments</option>
            {DEPARTMENT_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Select>
        </FormField>
        <div className="ml-auto text-xs text-ink-muted">
          Showing <span className="font-semibold text-ink">{summary.count}</span> budget
          {summary.count === 1 ? '' : 's'}
        </div>
      </div>

      {activeQuery.error && (
        <div className="mb-4">
          <ErrorBanner
            error={activeQuery.error}
            title="Could not load budgets"
            onRetry={activeQuery.refetch}
          />
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        loading={activeQuery.isLoading}
        error={!!activeQuery.error}
        empty={
          <EmptyState
            icon={FiPieChart}
            title="No budgets for this view"
            description={
              dept === ALL_DEPTS
                ? `No budgets have been created for ${year} yet.`
                : `No budgets for ${dept} in ${year} yet.`
            }
            action={
              <Button icon={FiPlus} onClick={() => setCreateOpen(true)}>
                Create Budget
              </Button>
            }
          />
        }
        onRowClick={(row) => setSelected(row)}
      />

      <BudgetForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultYear={year}
        defaultDepartment={dept === ALL_DEPTS ? '' : dept}
        onCreated={(created) => setSelected(created)}
      />

      <BudgetDetailModal
        open={!!selected}
        budgetId={selected?.id}
        fallback={selected}
        onClose={() => setSelected(null)}
      />
    </AppLayout>
  );
}
